import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MontageBuilderService } from '../../services/montage-builder.service';
import { OfferDetailService } from '../../services/offer-detail.service';
import { combineLatest, take, map, shareReplay, forkJoin, firstValueFrom, Subject, timer, retry, takeUntil } from 'rxjs';
import Konva from 'konva';
import { AssetTagService } from '../../services/asset-tag.service';
import { AssetTag } from '../../models/asset-tag.model';
import { AssetGroup } from '../../models/asset-group.model';
import { DEFAULT_SNACKBAR_CONFIG } from 'src/app/@shared/constants/site.constants';
import { UtilityService } from 'src/app/@shared/services/utility.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { v4 as uuidv4 } from 'uuid';
import { OfferProductService } from '../../services/offer-product.service';
import { Product } from '../../models/product.model';
import { AssetsListService } from '../../services/assets-list.service';
import { MontageAssetsListComponent } from '../montage-assets-list/montage-assets-list.component';
import { MontageHistoryPreviewComponent } from '../montage-history-preview/montage-history-preview.component';
import {
  getSelectionItemKey,
  MontageSelectionAsset,
  MontageSelectionProduct,
  assignJaiClientKeyToProduct,
  getChildAssetId,
  normalizeSelectionAsset,
  normalizeSelectionProduct,
  parseChildAssetClientKeys,
} from './models/montage-selection.model';
import {
  getSavedImageConfig,
  ImageEditorConfig,
  getUrlParams,
  parseSavedMontageConfigs,
  getAssetGroupName,
  shouldShowMontageHistory,
  buildJaiSavedConfigs,
} from './models/montage.model';
import { MontageHistoryDialogComponent } from '../montage-history-dialog/montage-history-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { ContextTypes } from '../../models/context-types.enum';

@Component({
  selector: 'app-new-montage-editor',
  templateUrl: './new-montage-editor.component.html',
  styleUrls: ['./new-montage-editor.component.scss']
})
export class NewMontageEditorComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private editorInitialized = false;
  private isActive = true;

  viewModel$ = combineLatest([
    this.offerDetailService.offer$,
    this.montageBuilderService.selectedOfferProducts$,
    this.montageBuilderService.selectedOfferAssets$,
    this.assetTagService.assetGroups$
  ]).pipe(
    map(([offer, selectedOfferProducts, selectedOfferAssets, assetGroups]) => {
      this.ImageEditorConfigId = offer?.ImageEditorConfigId;
      this.offerDetails = offer;
      this.selectedOfferProducts = selectedOfferProducts.map(normalizeSelectionProduct);
      this.selectedOfferAssets = selectedOfferAssets.map(normalizeSelectionAsset);
      this.assetGroup = assetGroups.find(
        (group: any) => group.AssetGroupName === getAssetGroupName(this.isOfferGroup)
      );
      return { offer, selectedOfferProducts, selectedOfferAssets };
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  isFetchingIAResponse = false;
  montageImages: any[] = [];
  imagesConfigs: any = {};
  layoutSelected = 'Layout';
  offerDetails: any = {};
  offerId = '';
  eventId = '';
  viewScale = 0.5;
  isProcessing = false;
  selectedOfferProducts: MontageSelectionProduct[] = [];
  selectedOfferAssets: MontageSelectionAsset[] = [];
  stage: any;
  private montageStageInitialized = false;
  private loadedExistingConfigId = '';
  assetGroup: AssetGroup | undefined;
  currentFileClientKey = '';
  currentFileId = '';
  montageShowVersionHistory: any = {};
  montageName = '';
  IsUniqueMontage = false;
  ImageEditorConfigId = '';
  currentDomainId = '';
  context: ContextTypes.OFFER | ContextTypes.OFFERGROUP = ContextTypes.OFFER;
  creativeId = '';
  JAIStatus = '';
  private jaiSettings: Record<string, any> = {};
  private savedMontageJSON: any = {};

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private montageBuilderService: MontageBuilderService,
    private offerDetailService: OfferDetailService,
    private assetTagService: AssetTagService<AssetTag, AssetGroup>,
    private matSnackBar: MatSnackBar,
    private dialog: MatDialog,
    private offerProductService: OfferProductService<Product>,
    private assetListService: AssetsListService,
    private utilityService: UtilityService
  ) {}

  get isOfferGroup(): boolean {
    return this.context === ContextTypes.OFFERGROUP;
  }

  ngOnInit(): void {
    this.offerId = this.montageBuilderService.offerId || this.route.snapshot.params['offerId'];
    this.eventId = this.route.snapshot.params['eventId'];
    this.montageBuilderService.offerId = this.offerId;
    this.context = this.resolveContext();
    this.montageBuilderService.montageEditorContext = this.context;
    this.creativeId = this.montageBuilderService.creativeId;

    this.viewModel$.pipe(take(1), takeUntil(this.destroy$)).subscribe(() => this.initializeEditor());
  }

  ngOnDestroy(): void {
    this.isActive = false;
    this.destroy$.next();
    this.destroy$.complete();
    if (this.stage) {
      this.stage.destroy();
      this.stage = null;
    }
    this.montageImages = [];
    this.imagesConfigs = {};
    this.montageBuilderService.creativeId = '';
    this.montageBuilderService.jaiMontageJSON = {};
  }

  private scheduleTimeout(callback: () => void, delay = 0): void {
    window.setTimeout(() => {
      if (this.isActive) {
        callback();
      }
    }, delay);
  }

  private resolveContext(): ContextTypes.OFFER | ContextTypes.OFFERGROUP {
    const eventType = this.montageBuilderService.montageGetEventTypeBehaviourSubject.value;
    if (eventType === 'OFFERGROUP' || this.router.url.includes(ContextTypes.OFFERGROUP)) {
      return ContextTypes.OFFERGROUP;
    }
    return ContextTypes.OFFER;
  }

  private initializeEditor(): void {
    if (this.editorInitialized) {
      return;
    }
    this.editorInitialized = true;

    const cachedJaiResponse = this.montageBuilderService.creativeMontageResponse
      ? JSON.parse(this.montageBuilderService.creativeMontageResponse)
      : null;

    if (cachedJaiResponse?.settings && Object.keys(cachedJaiResponse.settings).length) {
      this.jaiSettings = cachedJaiResponse.settings;
      this.savedMontageJSON = cachedJaiResponse;
      this.loadJaiMontage(this.jaiSettings);
      return;
    }

    if (this.creativeId) {
      this.loadJaiCreativeMontageDetails();
      return;
    }

    this.loadStandardMontageFlow();
  }

  private loadStandardMontageFlow(): void {
    if (!this.selectedOfferAssets.length && !this.selectedOfferProducts.length) {
      if (this.ImageEditorConfigId) {
        this.scheduleTimeout(() => this.loadExistingConfig());
      } else {
        this.applyMontageImagesFromSelection();
        this.scheduleTimeout(() => this.reloadMontageStage());
      }
      return;
    }

    const urlParams = getUrlParams([...this.selectedOfferAssets, ...this.selectedOfferProducts]);
    if (!urlParams) {
      this.continueWithoutHistory();
      return;
    }

    firstValueFrom(this.montageBuilderService.GetByAssets(urlParams).pipe(take(1))).then((montageHistory: any) => {
      if (!this.isActive) {
        return;
      }

      const montageHistoryData = montageHistory?.value?.find((item: any) => !!item.WebUrl);

      if (montageHistoryData && shouldShowMontageHistory(montageHistoryData, this.isOfferGroup, this.offerId, this.offerDetails?.ImageEditorConfigId)) {
        this.currentDomainId = montageHistoryData.DomainId;
        this.openMontageHistoryDialog(montageHistoryData);
        return;
      }

      this.continueWithoutHistory();
    });
  }

  private continueWithoutHistory(): void {
    if (this.ImageEditorConfigId) {
      this.scheduleTimeout(() => this.loadExistingConfig());
    } else {
      this.applyMontageImagesFromSelection();
      this.scheduleTimeout(() => this.reloadMontageStage());
    }
  }

  private openMontageHistoryDialog(montageHistoryData: any, fromEditor = false): void {
    this.montageShowVersionHistory = montageHistoryData;
    const dialogRef = this.dialog.open(MontageHistoryDialogComponent, {
      width: '40%',
      height: '40%',
      data: montageHistoryData,
      disableClose: true,
    });

    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result) => {
      if (fromEditor) {
        if (result === 'close') {
          this.montageStageInitialized = false;
          this.reloadMontageStage();
        } else {
          this.loadMontageShowVersionHistory();
        }
        return;
      }

      if (result === 'close') {
        this.applyMontageImagesFromSelection();
        this.scheduleTimeout(() => this.reloadMontageStage());
      } else {
        this.applyMontageImagesFromSelection(parseSavedMontageConfigs(montageHistoryData));
        this.reloadMontageStage();
      }
    });
  }

  private loadJaiMontage(settings: Record<string, any>): void {
    const clientKeys = Object.keys(settings);
    if (!clientKeys.length) {
      this.loadStandardMontageFlow();
      return;
    }

    forkJoin({
      assets: this.montageBuilderService.getAssetsByClientKeys(clientKeys),
      products: this.montageBuilderService.getProductsByClientKeys(clientKeys),
    }).pipe(takeUntil(this.destroy$)).subscribe(({ assets, products }) => {
      const productItems = products?.value ?? [];
      const merged = this.mergeSelections(
        this.selectedOfferAssets,
        (assets?.value ?? []).map(normalizeSelectionAsset),
        this.selectedOfferProducts,
        productItems.map((item: any, index: number) =>
          assignJaiClientKeyToProduct(normalizeSelectionProduct(item), productItems[index], clientKeys)
        )
      );
      this.selectedOfferAssets = merged.selectedOfferAssets;
      this.selectedOfferProducts = merged.selectedOfferProducts;
      this.applyMontageImagesFromSelection(buildJaiSavedConfigs(settings));
      this.scheduleTimeout(() => this.reloadMontageStage());
      this.checkMontageHistoryAfterJai();
    });
  }

  private loadJaiCreativeMontageDetails(): void {
    this.isFetchingIAResponse = true;
    this.isProcessing = true;

    this.montageBuilderService.getCreativeMontageDetails(this.creativeId).pipe(
      retry({ count: 5, delay: () => timer(6000), resetOnSuccess: true }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (data: any) => {
        this.isFetchingIAResponse = false;
        this.isProcessing = false;

        if (data?.Status === 'Completed') {
          this.JAIStatus = data.Status;
          this.loadStandardMontageFlow();
          return;
        }

        if (!data?.MontageJson) {
          return;
        }

        this.JAIStatus = data.Status;
        this.savedMontageJSON = JSON.parse(data.MontageJson);
        this.jaiSettings = this.savedMontageJSON.settings ?? {};
        this.loadJaiMontage(this.jaiSettings);
      },
      error: () => {
        this.isFetchingIAResponse = false;
        this.isProcessing = false;
      },
    });
  }

  private checkMontageHistoryAfterJai(): void {
    const urlParams = this.buildEditorHistoryUrlParams();
    if (!urlParams) {
      return;
    }

    firstValueFrom(this.montageBuilderService.GetByAssets(urlParams).pipe(take(1))).then((montageHistory: any) => {
      if (!this.isActive) {
        return;
      }

      const montageHistoryData = montageHistory?.value?.find((item: any) => !!item.WebUrl);

      if (montageHistoryData && shouldShowMontageHistory(montageHistoryData, this.isOfferGroup, this.offerId, this.offerDetails?.ImageEditorConfigId)) {
        const preparedHistory = this.prepareHistoryData(montageHistoryData);
        this.montageShowVersionHistory = preparedHistory;
        this.currentFileId = preparedHistory.Asset?.FileName ?? '';
        this.currentFileClientKey = preparedHistory.Asset?.Id ?? '';
        this.currentDomainId = preparedHistory.DomainId ?? '';
        this.montageBuilderService.montageShowVersionHistoryBehaviorSubject.next(preparedHistory);
        // Match legacy JAI history: keep JAI layout on dismiss, load history on apply.
        this.openMontageHistoryDialog(preparedHistory, true);
      }
    });
  }

  loadManualMontageEditor() {
    if (this.montageStageInitialized || !document.getElementById('container')) {
      return;
    }

    this.montageStageInitialized = true;
    this.sortImageAndLoadMontage();
  }

  loadExistingConfig() {
    if (!this.ImageEditorConfigId || this.loadedExistingConfigId === this.ImageEditorConfigId) {
      return;
    }

    this.montageBuilderService.getMontageImageEditorConfiguration(this.ImageEditorConfigId).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (config: any) => {
        const savedConfigs = parseSavedMontageConfigs(config);
        const clientKeys = parseChildAssetClientKeys(config.ChildAssets ?? []);
        this.IsUniqueMontage = config?.Detail?.IsUniqueMontage ?? false;

        if (!clientKeys.length) {
          this.applyMontageImagesFromSelection(savedConfigs);
          this.loadedExistingConfigId = this.ImageEditorConfigId;
          this.reloadMontageStage();
          return;
        }

        forkJoin({
          assets: this.montageBuilderService.getAssetsByClientKeys(clientKeys),
          products: this.montageBuilderService.getProductsByClientKeys(clientKeys),
        }).pipe(takeUntil(this.destroy$)).subscribe(({ assets, products }) => {
          const merged = this.mergeSelections(
            this.selectedOfferAssets,
            (assets?.value ?? []).map(normalizeSelectionAsset),
            this.selectedOfferProducts,
            (products?.value ?? []).map(normalizeSelectionProduct)
          );
          this.selectedOfferAssets = merged.selectedOfferAssets;
          this.selectedOfferProducts = merged.selectedOfferProducts;
          this.applyMontageImagesFromSelection(savedConfigs);
          this.loadedExistingConfigId = this.ImageEditorConfigId;
          this.reloadMontageStage();
        });
      },
    });
  }

  private reloadMontageStage(): void {
    if (!document.getElementById('container')) {
      return;
    }

    this.montageStageInitialized = false;
    this.loadManualMontageEditor();
  }

  private applyMontageImagesFromSelection(savedConfigs: ImageEditorConfig = {}): void {
    const { montageImages, imagesConfigs } = this.buildMontageImagesFromSelection(
      this.selectedOfferAssets,
      this.selectedOfferProducts,
      savedConfigs
    );
    this.montageImages = montageImages;
    this.imagesConfigs = imagesConfigs;
  }

  private mergeSelections(
    existingAssets: MontageSelectionAsset[],
    incomingAssets: MontageSelectionAsset[],
    existingProducts: MontageSelectionProduct[],
    incomingProducts: MontageSelectionProduct[]
  ): { selectedOfferAssets: MontageSelectionAsset[]; selectedOfferProducts: MontageSelectionProduct[] } {
    return {
      selectedOfferAssets: this.mergeSelectionItems(existingAssets, incomingAssets),
      selectedOfferProducts: this.mergeSelectionItems(existingProducts, incomingProducts),
    };
  }

  private mergeSelectionItems<T extends MontageSelectionAsset | MontageSelectionProduct>(
    existing: T[],
    incoming: T[]
  ): T[] {
    const merged = [...existing];
    const keyIndex = new Map<string, number>();

    merged.forEach((item, index) => {
      const key = getSelectionItemKey(item);
      if (key) {
        keyIndex.set(key, index);
      }
    });

    incoming.forEach((item) => {
      const key = getSelectionItemKey(item);
      if (!key) {
        merged.push(item);
        return;
      }

      const existingIndex = keyIndex.get(key);
      if (existingIndex !== undefined) {
        merged[existingIndex] = item;
      } else {
        keyIndex.set(key, merged.length);
        merged.push(item);
      }
    });

    return merged;
  }

  private buildMontageImagesFromSelection(
    selectedOfferAssets: MontageSelectionAsset[],
    selectedOfferProducts: MontageSelectionProduct[],
    savedConfigs: ImageEditorConfig = {}
  ): { montageImages: any[]; imagesConfigs: any } {
    const montageImages: any[] = [];
    const imagesConfigs: any = {};

    const buildImageConfig = (
      clientKey: string,
      imageId: string,
      layerIndex: number,
      existingConfig?: any
    ) => {
      const saved = getSavedImageConfig(savedConfigs, clientKey);

      return {
        x: saved?.x ?? existingConfig?.x ?? Math.floor(Math.random() * 20) + 180,
        y: saved?.y ?? existingConfig?.y ?? Math.floor(Math.random() * 20) + 180,
        refId: clientKey,
        scaleX: saved?.scaleX ?? existingConfig?.scaleX ?? 0.5,
        scaleY: saved?.scaleY ?? existingConfig?.scaleY ?? 0.5,
        rotation: saved?.rotation ?? existingConfig?.rotation ?? 0,
        layerIndex: saved?.layerIndex ?? existingConfig?.layerIndex ?? layerIndex,
        id: saved?.id || existingConfig?.id || imageId,
        ClientKey: clientKey,
      };
    };

    const upsertImage = (image: Omit<any, 'layerIndex'>, clientKey: string) => {
      if (!clientKey) {
        return;
      }

      const existingIndex = montageImages.findIndex(
        (montageImage) => montageImage.ClientKey === clientKey
      );

      if (existingIndex >= 0) {
        const existingConfig = imagesConfigs[clientKey];
        const layerIndex = existingConfig?.layerIndex ?? montageImages[existingIndex].layerIndex;

        montageImages[existingIndex] = {
          ...image,
          layerIndex: buildImageConfig(clientKey, image['id'], layerIndex, existingConfig).layerIndex,
        };
        imagesConfigs[clientKey] = buildImageConfig(
          clientKey,
          image['id'],
          layerIndex,
          existingConfig
        );
        return;
      }

      const layerIndex = montageImages.length;
      montageImages.push({
        ...image,
        layerIndex: buildImageConfig(clientKey, image['id'], layerIndex).layerIndex,
      });
      imagesConfigs[clientKey] = buildImageConfig(clientKey, image['id'], layerIndex);
    };

    selectedOfferAssets.forEach((asset) => {
      const clientKey = asset.Detail.ClientKey;
      const assetId = getChildAssetId(asset);
      upsertImage(
        {
          id: assetId,
          image: this.utilityService.withImageCacheBust(asset.Detail.WebUrl),
          name: asset.Detail.FileName,
          wicId: clientKey,
          ClientKey: clientKey,
        },
        clientKey
      );
    });

    selectedOfferProducts.forEach((product) => {
      const clientKey = product.Detail.ClientKey;
      const assetId = getChildAssetId(product);
      upsertImage(
        {
          id: assetId,
          image: this.utilityService.withImageCacheBust(product.FacingAsset.WebUrl),
          name: product.FacingAsset.FileName,
          wicId: clientKey,
          ClientKey: clientKey,
        },
        clientKey
      );
    });

    montageImages.sort((a, b) => a.layerIndex - b.layerIndex);

    return { montageImages, imagesConfigs };
  }

  sortImageAndLoadMontage() {
    const container = document.getElementById('container');
    if (!container) {
      return;
    }

    if (this.stage) {
      this.stage.destroy();
    }

    this.montageImages = [...this.montageImages].sort((a: any, b: any) => a.layerIndex - b.layerIndex);
    this.stage = new Konva.Stage({
      container: 'container',
      width: 1000,
      height: 1000,
    });

    this.stage.on('click', (e: any) => {
      const target = e.target;

      if (target === this.stage) {
        this.clearTransformers();
        return;
      }

      if (target.getClassName() === 'Image') {
        this.selectImageTransformer(target);
      }
    });

    if (this.montageImages.length > 0) {
      for (let i = 0; i < this.montageImages.length; i++) {
        const image = this.montageImages[i];
        const offset = ((i + 1) * 180) / 2;
        const savedConfig = this.imagesConfigs[image.ClientKey] || {};
        const layer = new Konva.Layer();
        this.stage.add(layer);

        Konva.Image.fromURL(image.image, (img) => {
          img.setAttrs({
            scaleX: 0.5,
            scaleY: 0.5,
            x: offset,
            y: offset,
            refId: image.ClientKey,
            ClientKey: image.ClientKey,
            id: savedConfig.id || image.id,
            draggable: true,
            strokeWidth: 1,
            rotation: savedConfig.rotation ?? 0,
            ...(savedConfig.x !== undefined ? { x: savedConfig.x } : {}),
            ...(savedConfig.y !== undefined ? { y: savedConfig.y } : {}),
            ...(savedConfig.scaleX !== undefined ? { scaleX: savedConfig.scaleX } : {}),
            ...(savedConfig.scaleY !== undefined ? { scaleY: savedConfig.scaleY } : {}),
          });
          layer.add(img);

          img.on('dragend dragmove transform transformend', () => this.selectImageTransformer(img));

          const tr = new Konva.Transformer({
            enabledAnchors: [
              'top-left',
              'top-right',
              'bottom-left',
              'bottom-right',
            ],
          });
          layer.add(tr);

          layer.draw();
          this.setImageConfig(img);
        });
      }
    }
  }

  selectImageTransformer(imgRef: any) {
    this.clearTransformers();

    let layer = imgRef.parent;
    let transformer = layer.children.filter(
      (lc: any) => lc.getClassName() == 'Transformer'
    );
    if (transformer[0]) {
      transformer[0].nodes([imgRef]);
    }
    this.setImageConfig(imgRef);
  }

  setImageConfig(imgRef: any) {
    const refId = imgRef.attrs.refId || imgRef.attrs.ClientKey;
    const montageImage = this.montageImages.find((image) => image.ClientKey === refId);
    this.imagesConfigs[refId] = {
      x: imgRef.x(),
      y: imgRef.y(),
      refId,
      scaleX: imgRef.scaleX(),
      scaleY: imgRef.scaleY(),
      rotation: imgRef.rotation() || 0,
      layerIndex: imgRef.parent.index,
      id: imgRef.attrs.id || imgRef.attrs.Id || montageImage?.id || '',
      ClientKey: imgRef.attrs.ClientKey ?? refId,
    };
  }

  setAllImgConfigs() {
    if (!this.stage?.children) {
      return;
    }

    for (const layer of this.stage.children) {
      for (const child of layer.children) {
        if (child.getClassName() === 'Image') {
          this.setImageConfig(child);
        }
      }
    }
  }

  clearTransformers() {
    for (const layer of this.stage.children) {
      let transformer = layer.children.filter(
        (lc: any) => lc.getClassName() == 'Transformer'
      );
      if (transformer[0]) {
        transformer[0].nodes([]);
      }
    }
  }

  saveUniqueMontage() {
    if (!this.montageImages.length) {
      return;
    }
    this.IsUniqueMontage = true;
    this.saveMontageConfig();
  }

  onDownload() {
    if (!this.stage) {
      return;
    }
    this.clearTransformers();
    this.montageName = this.getFileId();
    const dataURL = this.stage.toDataURL({ mimeType: 'image/png' });
    this.downloadURI(dataURL, `M_${this.montageName}.png`);
  }

  downloadURI(uri: string, name: string) {
    const link = document.createElement('a');
    link.download = name;
    link.href = uri;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  showVersionHistory() {
    if (!Object.keys(this.montageShowVersionHistory).length) {
      return;
    }

    const dialogRef = this.dialog.open(MontageHistoryPreviewComponent, {
      width: '40%',
      height: '600px',
      data: this.montageShowVersionHistory,
      disableClose: true,
    });

    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((data) => {
      if (data === 'close') {
        this.scheduleTimeout(() => this.reloadMontageStage());
      } else {
        this.loadMontageShowVersionHistory();
      }
    });
  }

  loadMontageShowVersionHistory() {
    this.applyHistoryChildAssets(this.montageShowVersionHistory, true);
  }

  layoutDesign(layoutSelected: string) {
    this.layoutSelected = layoutSelected;
  }

  close() {
    this.montageBuilderService.montageBuilderStatusBehaviorSubject.next(false);
    this.montageBuilderService.useNewMontageEditorBehaviorSubject.next(false);
    this.montageBuilderService.montageBuilderResponseBehaviorSubject.next({});
    this.montageBuilderService.selectedOfferAssetsBehaviorSubject.next([]);
    this.montageBuilderService.selectedOfferProductsBehaviorSubject.next([]);
    this.montageBuilderService.jaiMontageSelectedAssetsBehaviorSubject.next([]);
    this.montageBuilderService.jaiMontageSelectedProductsBehaviorSubject.next([]);
    this.montageBuilderService.creativeMontageResponse = '';
    this.montageBuilderService.jaiMontageJSON = {};
    // this.offerProductService.preSelectedProductsReload();
    this.assetListService.reload();
  }

  saveMontageConfig() {
    if (!this.montageImages.length || !this.stage) {
      return;
    }

    this.isProcessing = true;
    this.clearTransformers();
    this.setAllImgConfigs();

    const dataURL = this.stage.toDataURL({ mimeType: 'image/png' });
    const base64ImageContent = dataURL.replace(/^data:image\/(png|jpg);base64,/, '');
    const blob = this.base64ToBlob(base64ImageContent, 'image/png');
    const formData: FormData = new FormData();

    let fileName = `M_${this.getFileId()}.png`;
    if (this.IsUniqueMontage) {
      const suffix = this.offerDetails.Detail?.Id?.slice(-4) ?? uuidv4().slice(0, 4);
      fileName = `M_${this.getFileId()}_U_${suffix}.png`;
    } else {
      const permutations = this.getMontageAllPermutationOfClientKeys(
        this.getPermutations(this.getFileId().split('_').filter(Boolean)),
        false
      );
      const fileUnchanged = permutations.some((entry) => entry === this.currentFileClientKey);
      if (this.currentFileId && fileUnchanged) {
        fileName = this.currentFileId;
      }
    }

    const imageFile = new File([blob], fileName, { type: 'image/png' });
    formData.append('File', imageFile);
    formData.append('AssetGroupId', this.assetGroup?.Id || '');
    formData.append('EventId', this.route.snapshot.params['eventId']);

    if (this.isOfferGroup) {
      formData.append('OfferGroupId', this.offerId);
    } else {
      formData.append('OfferId', this.offerId);
    }

    if (!this.offerDetails.Detail?.Id || !this.offerDetails.Detail?.ClientKey) {
      this.isProcessing = false;
      return;
    }

    const createMontageConfiguration = this.buildMontageImageEditorConfiguration();
    createMontageConfiguration.IsUniqueMontage = this.IsUniqueMontage;
    createMontageConfiguration.VariantOfferIds = this.montageBuilderService.selectedOfferVariants.map(
      (variant: any) => variant.Id
    );
    if (this.selectedOfferProducts.length) {
      createMontageConfiguration.ProductIds = [
        ...new Set(this.selectedOfferProducts.map((product) => product.Id)),
      ];
    }

    this.montageBuilderService.uploadMontage(formData, this.IsUniqueMontage).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (event: any) => {
        createMontageConfiguration.AssetId = event.Id;
        this.montageBuilderService.createMontageImageEditorConfiguration(
          createMontageConfiguration,
          this.offerDetails.Detail.Id
        ).pipe(takeUntil(this.destroy$)).subscribe({
          next: () => {
            this.isProcessing = false;
            this.montageBuilderService.jaiMontageJSON = {};
            this.offerDetailService.reload();
            this.matSnackBar.open(
              `Montage saved successfully`,
              'OK',
              DEFAULT_SNACKBAR_CONFIG
            );
            this.close();
          },
          error: () => {
            this.isProcessing = false;
            this.matSnackBar.open(
              `Montage Image Editor Configuration creation failed`,
              'OK',
              DEFAULT_SNACKBAR_CONFIG
            );
          },
        });
      },
      error: () => {
        this.isProcessing = false;
        this.matSnackBar.open(
          `Montage Image Editor Configuration creation failed`,
          'OK',
          DEFAULT_SNACKBAR_CONFIG
        );
      },
    });
  }

  buildMontageImageEditorConfiguration() {
    let createMontageConfiguration: any = {};
    let Detail: any = {
      ClientKey: this.offerDetails.Detail.ClientKey,
      Name: this.offerDetails.Detail.Name,
      Config: JSON.stringify(this.imagesConfigs),
      IsUniqueMontage: this.IsUniqueMontage,
    };

    if (this.IsUniqueMontage) {
      Detail.ClientKey = uuidv4();
    } else if (this.currentDomainId) {
      Detail.Id = this.currentDomainId;
    } else if (this.ImageEditorConfigId) {
      Detail.Id = this.ImageEditorConfigId;
    }

    if (this.creativeId && this.JAIStatus !== 'Completed' && this.savedMontageJSON) {
      const settings = { ...this.jaiSettings };
      Object.keys(settings).forEach((clientKey) => {
        settings[clientKey] = {
          ...this.imagesConfigs[clientKey],
          refId: clientKey,
        };
      });
      this.savedMontageJSON.settings = settings;
      createMontageConfiguration.MontageJson = JSON.stringify(this.savedMontageJSON);
    }

    createMontageConfiguration.Detail = { ...Detail };

    if (this.isOfferGroup) {
      createMontageConfiguration.OfferGroupId = this.offerDetails.Detail.Id;
      createMontageConfiguration.EventOfferType = 'OFFERGROUP';
    } else {
      createMontageConfiguration.OfferId = this.offerDetails.Detail.Id;
      createMontageConfiguration.EventOfferType = 'OFFER';
    }

    createMontageConfiguration.ChildAssets = this.montageImages
      .map((montageImage) => ({
        AssetId: montageImage.id,
        Config: JSON.stringify(this.imagesConfigs[montageImage.ClientKey] ?? {}),
      }))
      .filter((child) => !!child.AssetId);

    return createMontageConfiguration;
  }

  base64ToBlob(base64: any, mime: any) {
    mime = mime || '';
    var sliceSize = 1024;
    var byteChars = window.atob(base64);
    var byteArrays = [];

    for (var offset = 0, len = byteChars.length; offset < len; offset += sliceSize) {
      var slice = byteChars.slice(offset, offset + sliceSize);
      var byteNumbers = new Array(slice.length);
      for (var i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      var byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    return new Blob(byteArrays, { type: mime });
  }

  onReorder() {
    this.montageImages = this.sortImagesByConfig(this.montageImages);
    if (!this.stage?.children) {
      return;
    }

    for (const layer of this.stage.children) {
      const image = layer.children.find((child: any) => child.getClassName() === 'Image');
      if (image) {
        const clientKey = image.attrs.ClientKey || image.attrs.refId;
        layer.setZIndex(this.imagesConfigs[clientKey]?.layerIndex ?? 0);
      }
    }
  }

  onDelete(selectedImage: any) {
    const clientKey = selectedImage.ClientKey || selectedImage.wicId;
    this.montageImages = this.montageImages.filter((image) => image.ClientKey !== clientKey);
    this.selectedOfferProducts = this.selectedOfferProducts.filter(
      (product) => product.Detail.ClientKey !== clientKey
    );
    this.selectedOfferAssets = this.selectedOfferAssets.filter(
      (asset) => asset.Detail.ClientKey !== clientKey
    );
    const { [clientKey]: _removed, ...remainingConfigs } = this.imagesConfigs;
    this.imagesConfigs = remainingConfigs;

    if (this.stage) {
      this.stage.destroy();
      this.stage = null;
    }
    this.montageStageInitialized = false;
    this.checkVersionHistory(true);
  }

  openSearchDialog(): void {
    this.montageBuilderService.montageOfferSelectedFromEventBehaviourSubject.next(
      this.route.snapshot.params['offerId']
    );

    const dialogRef = this.dialog.open(MontageAssetsListComponent, {
      width: '100%',
      height: '100%',
      data: {},
      disableClose: true,
    });

    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result) => {
      if (!result?.length) {
        return;
      }

      const newAssetIds = result
        .map((item: any) => item?.Detail?.Id ?? item?.Id)
        .filter(Boolean);
      const urlParams = this.buildEditorHistoryUrlParams(newAssetIds);

      this.montageBuilderService.GetByAssets(urlParams).pipe(takeUntil(this.destroy$)).subscribe({
        next: (data: any) => {
          const historyItem = data?.value?.[0];

          if (historyItem?.DomainId) {
            this.currentDomainId = historyItem.DomainId;
          }

          if (
            historyItem &&
            shouldShowMontageHistory(historyItem, this.isOfferGroup, this.offerId, this.offerDetails?.ImageEditorConfigId)
          ) {
            const preparedHistory = this.prepareHistoryData(historyItem);
            this.montageShowVersionHistory = preparedHistory;
            this.currentFileId = preparedHistory.Asset?.FileName ?? '';
            this.currentFileClientKey = preparedHistory.Asset?.Id ?? '';
            this.currentDomainId = preparedHistory.DomainId ?? '';
            this.montageBuilderService.montageShowVersionHistoryBehaviorSubject.next(preparedHistory);
            this.applyHistoryChildAssets(preparedHistory, false, () => {
              this.openMontageHistoryDialog(preparedHistory, true);
            });
            return;
          }

          if (!data?.value?.length) {
            this.currentDomainId = '';
            this.ImageEditorConfigId = '';
          }

          let layerIndex = Object.keys(this.imagesConfigs).length;
          result.forEach((item: any) => {
            this.addAssetToMontage(item, layerIndex);
            layerIndex += 1;
          });
          this.montageStageInitialized = false;
          this.reloadMontageStage();
        },
      });
    });
  }

  private addAssetToMontage(selection: any, layerIndex: number): void {
    const detail = selection?.Detail ?? selection;
    if (!detail?.Id || !detail?.WebUrl || !detail?.FileType?.includes('image')) {
      return;
    }

    const asset = normalizeSelectionAsset({
      DomainId: detail.Id,
      Detail: {
        ClientKey: detail.ClientKey ?? '',
        WebUrl: detail.WebUrl,
        FileName: detail.FileName ?? detail.Name ?? '',
      },
    });
    const clientKey = asset.Detail.ClientKey;
    if (!clientKey || this.montageImages.some((image) => image.ClientKey === clientKey)) {
      return;
    }

    const assetId = getChildAssetId(asset);
    this.montageImages = [
      ...this.montageImages,
      {
        id: assetId,
        image: this.utilityService.withImageCacheBust(asset.Detail.WebUrl),
        name: asset.Detail.FileName,
        wicId: clientKey,
        ClientKey: clientKey,
      },
    ];
    this.imagesConfigs = {
      ...this.imagesConfigs,
      [clientKey]: {
        x: Math.floor(Math.random() * 20) + 180,
        y: Math.floor(Math.random() * 20) + 180,
        refId: clientKey,
        scaleX: 0.5,
        scaleY: 0.5,
        rotation: 0,
        layerIndex,
        id: assetId,
        ClientKey: clientKey,
      },
    };
    this.selectedOfferAssets = [...this.selectedOfferAssets, asset];
  }

  private buildEditorHistoryUrlParams(additionalAssetIds: string[] = []): string {
    const assetIds = new Set<string>();
    additionalAssetIds.filter(Boolean).forEach((id) => assetIds.add(id));
    this.montageImages.forEach((image) => {
      if (image.id) {
        assetIds.add(image.id);
      }
    });
    return [...assetIds].map((id) => `assetIds=${id}`).join('&');
  }

  private getHistoryClientKeys(historyItem: any): string[] {
    const savedConfigs = parseSavedMontageConfigs(historyItem);
    return [
      ...new Set([
        ...Object.keys(savedConfigs),
        ...parseChildAssetClientKeys(historyItem.ChildAssets ?? []),
      ]),
    ].filter(Boolean);
  }

  private getHistoryAssetId(historyItem: any, clientKey: string): string {
    for (const childAsset of historyItem.ChildAssets ?? []) {
      try {
        const parsed = JSON.parse(childAsset.Config);
        const layout = parsed.montageImageConfig || parsed;
        const key = layout.ClientKey || layout.refId || parsed.montageImageDetails?.wicId;
        if (key !== clientKey) {
          continue;
        }
        return layout.id || parsed.montageImageDetails?.id || childAsset.AssetId || '';
      } catch {
        // ignore invalid config
      }
    }
    return '';
  }

  private getLegacyHistoryImageDetails(historyItem: any, clientKey: string): any | null {
    for (const childAsset of historyItem.ChildAssets ?? []) {
      try {
        const parsed = JSON.parse(childAsset.Config);
        if (!parsed.montageImageDetails?.image) {
          continue;
        }
        const key =
          parsed.montageImageConfig?.ClientKey ||
          parsed.montageImageConfig?.refId ||
          parsed.montageImageDetails?.ClientKey ||
          parsed.montageImageDetails?.wicId;
        if (key === clientKey) {
          return parsed.montageImageDetails;
        }
      } catch {
        // ignore invalid config
      }
    }
    return null;
  }

  private fillHistoryImagesFromExisting(
    montageImages: any[],
    imagesConfigs: any,
    savedConfigs: ImageEditorConfig,
    historyItem: any,
    existingByClientKey: Map<string, any>
  ): any[] {
    const result = [...montageImages];

    Object.keys(savedConfigs).forEach((clientKey) => {
      if (result.some((image) => image.ClientKey === clientKey && image.image)) {
        return;
      }

      const saved = getSavedImageConfig(savedConfigs, clientKey);
      if (!saved) {
        return;
      }

      const assetId = saved.id || this.getHistoryAssetId(historyItem, clientKey);
      const existing = existingByClientKey.get(clientKey);
      const legacyDetails = this.getLegacyHistoryImageDetails(historyItem, clientKey);
      const imageUrl = existing?.image || legacyDetails?.image || '';

      if (!imageUrl) {
        return;
      }

      const imageEntry = {
        id: assetId || existing?.id || legacyDetails?.id || '',
        image: this.utilityService.withImageCacheBust(imageUrl),
        name: existing?.name || legacyDetails?.name || legacyDetails?.imageName || clientKey,
        wicId: clientKey,
        ClientKey: clientKey,
      };

      const existingIndex = result.findIndex((image) => image.ClientKey === clientKey);
      if (existingIndex >= 0) {
        result[existingIndex] = { ...result[existingIndex], ...imageEntry };
      } else {
        result.push(imageEntry);
      }

      imagesConfigs[clientKey] = {
        ...(imagesConfigs[clientKey] || {}),
        ...saved,
        ClientKey: clientKey,
        refId: clientKey,
        id: assetId || saved.id || existing?.id || '',
      };
    });

    return result;
  }

  private applyHistoryChildAssets(
    historyItem: any,
    replaceExisting: boolean,
    onComplete?: () => void
  ): void {
    const savedConfigs = parseSavedMontageConfigs(historyItem);
    const clientKeys = this.getHistoryClientKeys(historyItem);

    if (!clientKeys.length) {
      onComplete?.();
      return;
    }

    const existingByClientKey = new Map(
      this.montageImages.map((image) => [image.ClientKey, image])
    );

    forkJoin({
      assets: this.montageBuilderService.getAssetsByClientKeys(clientKeys),
      products: this.montageBuilderService.getProductsByClientKeys(clientKeys),
    }).pipe(takeUntil(this.destroy$)).subscribe(({ assets, products }) => {
      const productItems = products?.value ?? [];
      const normalizedAssets = (assets?.value ?? []).map(normalizeSelectionAsset);
      const normalizedProducts = productItems.map((item: any, index: number) =>
        assignJaiClientKeyToProduct(normalizeSelectionProduct(item), productItems[index], clientKeys)
      );

      let { montageImages, imagesConfigs } = this.buildMontageImagesFromSelection(
        normalizedAssets,
        normalizedProducts,
        savedConfigs
      );

      montageImages = this.fillHistoryImagesFromExisting(
        montageImages,
        imagesConfigs,
        savedConfigs,
        historyItem,
        existingByClientKey
      );

      if (replaceExisting) {
        this.montageImages = montageImages;
        this.imagesConfigs = imagesConfigs;
      } else {
        const historyKeys = new Set(montageImages.map((image) => image.ClientKey));
        const keptExisting = this.montageImages.filter((image) => !historyKeys.has(image.ClientKey));
        this.montageImages = [...keptExisting, ...montageImages];
        this.imagesConfigs = { ...this.imagesConfigs, ...imagesConfigs };
      }

      this.montageStageInitialized = false;
      this.reloadMontageStage();
      onComplete?.();
    });
  }

  private prepareHistoryData(historyItem: any): any {
    const data = {
      ...historyItem,
      Asset: historyItem.Asset ? { ...historyItem.Asset } : historyItem.Asset,
    };

    try {
      if (data.WebUrl) {
        const url = new URL(data.WebUrl);
        url.searchParams.set('time', Date.now().toString());
        data.WebUrl = url.toString();
      }
      if (data.Asset?.WebUrl) {
        const assetUrl = new URL(data.Asset.WebUrl);
        assetUrl.searchParams.set('time', Date.now().toString());
        data.Asset.WebUrl = assetUrl.toString();
      }
    } catch {
      // ignore invalid urls
    }

    return data;
  }

  private checkVersionHistory(fromEditor = false): void {
    const urlParams = this.buildEditorHistoryUrlParams();

    this.montageBuilderService.GetByAssets(urlParams).pipe(takeUntil(this.destroy$)).subscribe({
      next: (data: any) => {
        const historyItem = data?.value?.[0];

        if (historyItem?.DomainId) {
          this.currentDomainId = historyItem.DomainId;
        }

        if (
          historyItem &&
          shouldShowMontageHistory(historyItem, this.isOfferGroup, this.offerId, this.offerDetails?.ImageEditorConfigId)
        ) {
          const preparedHistory = this.prepareHistoryData(historyItem);
          this.montageShowVersionHistory = preparedHistory;
          this.currentFileId = preparedHistory.Asset?.FileName ?? '';
          this.currentFileClientKey = preparedHistory.Asset?.Id ?? '';
          this.currentDomainId = preparedHistory.DomainId ?? '';
          this.montageBuilderService.montageShowVersionHistoryBehaviorSubject.next(preparedHistory);
          this.openMontageHistoryDialog(preparedHistory, fromEditor);
          return;
        }

        if (!data?.value?.length) {
          this.currentDomainId = '';
          this.ImageEditorConfigId = '';
        }

        this.montageStageInitialized = false;
        this.reloadMontageStage();
      },
    });
  }

  private sortImagesByConfig(montageImages: any[]): any[] {
    return [...montageImages].sort((a, b) => {
      const aIndex = this.imagesConfigs[a.ClientKey]?.layerIndex ?? 0;
      const bIndex = this.imagesConfigs[b.ClientKey]?.layerIndex ?? 0;
      return aIndex - bIndex;
    });
  }

  getPermutations(arr: string[]): string[][] {
    if (arr.length === 1) {
      return [arr];
    }

    const result: string[][] = [];
    for (let i = 0; i < arr.length; i++) {
      const current = arr[i];
      const remaining = arr.slice(0, i).concat(arr.slice(i + 1));
      const remainingPermutations = this.getPermutations(remaining);
      for (const perm of remainingPermutations) {
        result.push([current, ...perm]);
      }
    }
    return result;
  }

  getMontageAllPermutationOfClientKeys(result: string[][], uniqueMontageStatus: boolean): string[] {
    return result.map((arr) => {
      const sorted = [...arr].sort().join('_');
      return uniqueMontageStatus ? `M_${sorted}_U` : `M_${sorted}`;
    });
  }

  getFileId(): string {
    let fileKeys: string[] = [...this.montageImages].map((m) => {
      let imgName: string = m.id ? m.id : m.imageName;
      if (!imgName) return '';
      imgName = imgName.toLowerCase().trim();
      if (imgName.indexOf('.') != -1) {
        let imgSplit = imgName.split('.');
        imgSplit.pop();
        imgName = imgSplit.join('');
      }
      return imgName.slice(-10);
    });
    return fileKeys && fileKeys.length ? fileKeys.sort().join('_') : '';
  }
}
